import * as React from "react";
import { cn } from "./cn";
import { Input } from "./input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

/**
 * Date-of-birth entry — Day / Month / Year, never a calendar.
 *
 * A native `<input type="date">` opens on the CURRENT month, so a birthday is
 * reached by paging back one month at a time: dozens of clicks for a child,
 * hundreds for an adult leader. Three controls cost three interactions whatever
 * the year, and naming the month removes the day/month ambiguity that a typed
 * date carries here (03/04 is two different days depending on where you grew
 * up). The year is TYPED rather than picked: a hundred-item dropdown is its own
 * scrolling problem, and "1978" is four keystrokes.
 *
 * Emits an ISO `YYYY-MM-DD` string — exactly what the date input produced, so
 * nothing downstream changes — and "" until the three parts make a real date,
 * which keeps every existing "is this answer blank?" required-check working.
 */

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

type Parts = { day: string; month: string; year: string };

const EMPTY: Parts = { day: "", month: "", year: "" };

function splitIso(value: string): Parts {
  const m = ISO_RE.exec(value.trim());
  return m ? { year: m[1]!, month: m[2]!, day: m[3]! } : EMPTY;
}

/** Days in a month, with a LEAP year assumed while the year is still blank —
 *  so picking 29 February first doesn't vanish before the year is typed. */
function daysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) return 31;
  const y = Number.isFinite(year) && year > 0 ? year : 2024;
  return new Date(y, month, 0).getDate();
}

export interface DateOfBirthInputProps {
  /** Lands on the Day control, so the surrounding `<Label htmlFor>` focuses it. */
  id?: string;
  /** ISO `YYYY-MM-DD`, or "" when not yet answered. */
  value: string;
  onChange: (value: string) => void;
  /** Announced for the group as a whole, e.g. "Child's date of birth". */
  label?: string;
  required?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  /** Oldest year offered. Default: 100 years ago. */
  minYear?: number;
  /** Newest year offered. Default: this year — nobody is born tomorrow. */
  maxYear?: number;
  className?: string;
}

export const DateOfBirthInput = React.forwardRef<HTMLDivElement, DateOfBirthInputProps>(
  (
    {
      id,
      value,
      onChange,
      label,
      required,
      invalid,
      disabled,
      minYear,
      maxYear,
      className,
    },
    ref,
  ) => {
    const thisYear = new Date().getFullYear();
    const newest = maxYear ?? thisYear;
    const oldest = minYear ?? thisYear - 100;

    const [parts, setParts] = React.useState<Parts>(() => splitIso(value));

    const join = React.useCallback(
      (p: Parts): string => {
        if (p.day === "" || p.month === "" || !/^\d{4}$/.test(p.year)) return "";
        const year = Number(p.year);
        if (year < oldest || year > newest) return "";
        if (Number(p.day) > daysInMonth(year, Number(p.month))) return "";
        return `${p.year}-${p.month}-${p.day}`;
      },
      [oldest, newest],
    );

    /* The value can also change from OUTSIDE — a prefill from a previous
       registration, or the form being reset. Re-read it only when it disagrees
       with what these three controls currently spell, so a half-finished entry
       (which reports "") is never wiped by its own onChange. */
    React.useEffect(() => {
      setParts((prev) => (join(prev) === value ? prev : splitIso(value)));
    }, [value, join]);

    function commit(next: Parts) {
      /* 31 September can be reached by changing the month under a chosen day.
         The day is dropped rather than silently clamped: the placeholder coming
         back is visible, a date the parent never picked is not. */
      const limit = daysInMonth(Number(next.year), Number(next.month));
      const settled = next.day !== "" && Number(next.day) > limit ? { ...next, day: "" } : next;
      setParts(settled);
      onChange(join(settled));
    }

    const dayLimit = daysInMonth(Number(parts.year), Number(parts.month));
    const invalidFlag = invalid ? true : undefined;

    return (
      <div
        ref={ref}
        role="group"
        aria-label={label}
        className={cn(
          "grid grid-cols-[minmax(0,1fr)_minmax(0,1.7fr)_minmax(0,1.1fr)] gap-2",
          className,
        )}
      >
        <Select
          value={parts.day === "" ? undefined : parts.day}
          onValueChange={(v) => commit({ ...parts, day: v })}
          disabled={disabled}
        >
          <SelectTrigger id={id} aria-label="Day" aria-required={required} aria-invalid={invalidFlag}>
            <SelectValue placeholder="Day" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: dayLimit }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => (
              <SelectItem key={d} value={d}>
                {Number(d)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={parts.month === "" ? undefined : parts.month}
          onValueChange={(v) => commit({ ...parts, month: v })}
          disabled={disabled}
        >
          <SelectTrigger
            id={id === undefined ? undefined : `${id}-month`}
            aria-label="Month"
            aria-required={required}
            aria-invalid={invalidFlag}
          >
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((name, i) => (
              <SelectItem key={name} value={String(i + 1).padStart(2, "0")}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          id={id === undefined ? undefined : `${id}-year`}
          type="text"
          inputMode="numeric"
          autoComplete="bday-year"
          maxLength={4}
          placeholder="Year"
          aria-label="Year"
          aria-required={required}
          aria-invalid={invalidFlag}
          disabled={disabled}
          value={parts.year}
          onChange={(e) => commit({ ...parts, year: e.target.value.replace(/\D/g, "").slice(0, 4) })}
        />
      </div>
    );
  },
);
DateOfBirthInput.displayName = "DateOfBirthInput";
